import { backoff } from '@cfc/exponential-backoff'

import { CURRICULUM_SERVICE_NO_JWT_ERROR } from '../../components/AppWrapper/constants'
import { CURRICULUM_SERVICE_URL } from '../config'

const PAGE_CONTENT_PATH = '/v1/page-content'

/**
 * Builds the query string for a `/v1/page-content` request.
 *
 * The Contentful entry id is passed as `externalId` because the page-content
 * endpoint has no route associated with it and resolves content directly by
 * its external (Contentful) identifier.
 */
export const buildPageContentQuery = ({
  externalId,
  siteId,
  contentGroupCode,
  resolutionMode,
  draftMode,
  locale,
}) => {
  const params = new URLSearchParams()
  params.append('externalId', externalId)
  if (resolutionMode) {
    params.append('resolutionMode', resolutionMode)
  }
  if (siteId != null && siteId !== '') {
    params.append('siteId', String(siteId))
  }
  if (contentGroupCode) {
    params.append('contentGroupCode', contentGroupCode)
  }
  // draftMode defaults to false; always send the resolved value explicitly.
  params.append('draftMode', draftMode ? 'true' : 'false')
  if (locale) {
    params.append('locale', locale)
  }
  return params.toString()
}

/**
 * Fetches page content for a Contentful entry from the curriculum service
 * `GET /v1/page-content` endpoint, authenticating with the supplied JWT.
 *
 * Fails (throws) when no JWT is available, since the curriculum service
 * requires authentication — callers requesting curriculum-service content
 * without a token should not silently fall back to unauthenticated content.
 *
 * @param {Object} options
 * @param {string} options.externalId        - Contentful entry id
 * @param {string} [options.jwt]             - raw JWT (no "Bearer " prefix)
 * @param {string|number|null} [options.siteId]
 * @param {string|null} [options.contentGroupCode]
 * @param {string} [options.resolutionMode]  - 'singleSite' | 'multiSite'
 * @param {boolean} [options.draftMode]
 * @param {string} [options.locale]
 * @returns {Promise<Object>} the curriculum-service JSON response
 */
export const fetchPageContent = async ({
  externalId,
  jwt,
  siteId,
  contentGroupCode,
  resolutionMode,
  draftMode,
  locale,
}) => {
  if (!jwt) {
    throw new Error(CURRICULUM_SERVICE_NO_JWT_ERROR)
  }

  const query = buildPageContentQuery({
    externalId,
    siteId,
    contentGroupCode,
    resolutionMode,
    draftMode,
    locale,
  })
  const url = `${CURRICULUM_SERVICE_URL}${PAGE_CONTENT_PATH}?${query}`

  const response = await backoff(() =>
    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Authorization: `Bearer ${jwt}`,
      },
    }),
  )

  if (!response.ok) {
    throw new Error(
      `Failed to get page content from curriculum service: ${response.status} (${response.statusText})`,
    )
  }

  return await response.json()
}
